import * as z from 'zod';

/**
 * Merchant domain schemas (ported from cpanel `features/merchants` — logic
 * 1:1, Zod as the single source of truth per this repo's conventions).
 *
 * Wire tolerance rule (kept from the old `model/types.ts`): the vertical /
 * legal-form / status enums ARE exact JSON-schema enums on the backend, but
 * responses are rendered tolerantly — an unknown future member falls back to
 * its raw token instead of crashing. Hence API schemas use `z.string()`
 * while FORM schemas use strict `z.enum(...)`.
 */

export const merchantVerticalSchema = z.enum([
  'MERCHANT_VERTICAL_UNSPECIFIED',
  'MERCHANT_VERTICAL_EATS',
  'MERCHANT_VERTICAL_PHARMA',
  'MERCHANT_VERTICAL_DELIVERY',
]);
export type MerchantVertical = z.infer<typeof merchantVerticalSchema>;

export const configuredMerchantVerticalSchema = merchantVerticalSchema.exclude([
  'MERCHANT_VERTICAL_UNSPECIFIED',
]);
export type ConfiguredMerchantVertical = z.infer<typeof configuredMerchantVerticalSchema>;

export const legalFormSchema = z.enum(['LEGAL_FORM_INDIVIDUAL', 'LEGAL_FORM_COMPANY']);
export type LegalForm = z.infer<typeof legalFormSchema>;

export const employeeRoleSchema = z.enum(['merchant-admin', 'manager', 'cashier']);
export type EmployeeRole = z.infer<typeof employeeRoleSchema>;

/** Brand row. `merchant_id` is the canonical id (falls back to `id`). */
export const merchantSchema = z
  .object({
    id: z.string().optional(),
    merchant_id: z.string().optional(),
    name: z.string().optional(),
    logo_url: z.string().nullish(),
    legal_name: z.string().nullish(),
    legal_address: z.string().nullish(),
    business_name: z.string().nullish(),
    address: z.string().nullish(),
    avatar_image: z.string().nullish(),
    business_image: z.string().nullish(),
    status: z.string().optional(),
    verticals: z.array(z.string()).optional(),
    legal_form: z.string().optional(),
    inn: z.string().optional(),
    phone: z.string().optional(),
    admin_phone: z.string().optional(),
    menu_source: z.string().optional(),
    delivery_provider: z.string().optional(),
    send_orders_to_noor: z.boolean().optional(),
    /** Brand-wide VAT rate in %; 0 = not a VAT payer. Never null. */
    vat_percent: z.number().optional(),
    /** Noor commission in %; 0 means no commission. */
    commission_percent: z.number().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
  })
  .loose();
export type Merchant = z.infer<typeof merchantSchema>;

export const merchantListMetaSchema = z
  .object({
    total: z.number().optional(),
    page: z.number().optional(),
    page_count: z.number().optional(),
    timestamp: z.string().optional(),
  })
  .loose();
export type MerchantListMeta = z.infer<typeof merchantListMetaSchema>;

export const merchantListSchema = z
  .object({
    list: z.array(merchantSchema).default([]),
    meta: merchantListMetaSchema.optional(),
  })
  .loose();
export type MerchantList = z.infer<typeof merchantListSchema>;

export const employeeSchema = z
  .object({
    employee_id: z.string().optional(),
    merchant_id: z.string().optional(),
    phone: z.string().optional(),
    role: z.string().optional(),
    status: z.string().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
  })
  .loose();
export type Employee = z.infer<typeof employeeSchema>;

export const employeeListSchema = z
  .object({
    list: z.array(employeeSchema).default([]),
    meta: merchantListMetaSchema.optional(),
  })
  .loose();
export type EmployeeList = z.infer<typeof employeeListSchema>;

/**
 * Create (`POST /api/v1/admin/merchants`). An admin is mandatory — a brand
 * cannot exist without one, so it is attached in the same transaction. Named
 * by `admin_phone` here (the UI does not offer the `admin_employee_id` path;
 * that identity picker does not exist yet). The role is never accepted: the
 * first admin is always merchant-admin. There is no `legal_logo_url` — the
 * Merchant tab carries a single logo upload (`logo_url`).
 */
export const createMerchantPayloadSchema = z.object({
  name: z.string().min(1),
  logo_url: z.string().optional(),
  verticals: z.array(configuredMerchantVerticalSchema).min(1),
  admin_phone: z.string().optional(),
  admin_employee_id: z.string().optional(),
  legal_form: legalFormSchema,
  inn: z.string().min(1),
  legal_name: z.string().optional(),
  legal_address: z.string().optional(),
});
export type CreateMerchantPayload = z.infer<typeof createMerchantPayloadSchema>;

/** PUT: replaces the merchant profile, including verticals. Cleared text/image fields are sent as `null`. */
export const updateMerchantPayloadSchema = z.object({
  name: z.string().optional(),
  logo_url: z.string().nullable().optional(),
  legal_name: z.string().nullable().optional(),
  legal_address: z.string().nullable().optional(),
  status: z.string().optional(),
  legal_form: legalFormSchema.optional(),
  inn: z.string().optional(),
  verticals: z.array(configuredMerchantVerticalSchema),
});
export type UpdateMerchantPayload = z.infer<typeof updateMerchantPayloadSchema>;

/**
 * Update a brand employee (`PATCH /api/v1/admin/merchant-employees/{id}`).
 * Every field is optional; an omitted field is left unchanged. An empty role
 * means "don't touch the role" — the backend treats it as absent.
 */
export const updateEmployeePayloadSchema = z.object({
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  role: z.string().optional(),
});
export type UpdateEmployeePayload = z.infer<typeof updateEmployeePayloadSchema>;

/** Attach an existing identity to a brand — the only way the link is created. */
export const attachEmployeePayloadSchema = z.object({
  merchant_id: z.string().min(1),
  employee_id: z.string().optional(),
  new_identity: z
    .object({
      phone: z.string().min(1),
      role: z.string().min(1),
      first_name: z.string().optional(),
      last_name: z.string().optional(),
    })
    .optional(),
});
export type AttachEmployeePayload = z.infer<typeof attachEmployeePayloadSchema>;

/* ── Integration ─────────────────────────────────────────────────────── */

export const merchantIntegrationSchema = z
  .object({
    merchant_id: z.string().optional(),
    menu_source: z.string().optional(),
    delivery_provider: z.string().optional(),
    client_id: z.string().optional(),
    client_secret: z.string().optional(),
    vat_percent: z.number().optional(),
    commission_percent: z.number().optional(),
  })
  .loose()
  .passthrough();
export type MerchantIntegration = z.infer<typeof merchantIntegrationSchema>;

export const updateMerchantIntegrationPayloadSchema = z.object({
  menu_source: z.string().min(1),
  delivery_provider: z.string().min(1),
  send_orders_to_noor: z.boolean(),
});

/**
 * Select options for the Eats tab — `GET
 * /api/v1/merchants/integration-options` answers `{data: {menu_sources:
 * ("internal" | "delever" | "iko")[], delivery_providers: ("noor" |
 * "delever" | "iko")[], send_orders_to_noor_delivery_providers: ("delever" |
 * "iko")[]}}`. The last one lists the delivery providers for which the
 * "send orders to Noor" switch is meaningful. Arrays stay `z.string()`
 * (wire tolerance rule: an unknown future member must not crash parsing).
 */
export const integrationOptionsSchema = z
  .object({
    menu_sources: z.array(z.string()).default([]),
    delivery_providers: z.array(z.string()).default([]),
    send_orders_to_noor_delivery_providers: z.array(z.string()).default([]),
  })
  .loose();
export type IntegrationOptions = z.infer<typeof integrationOptionsSchema>;

export const merchantIntegrationCredentialsSchema = z
  .object({
    merchant_id: z.string().optional(),
    provider: z.string().optional(),
    attached: z.boolean().optional(),
    attached_at: z.string().optional(),
    values: z.record(z.string(), z.record(z.string(), z.string())).default({}),
  })
  .loose();
export type MerchantIntegrationCredentials = z.infer<
  typeof merchantIntegrationCredentialsSchema
>;
export type UpdateMerchantIntegrationPayload = z.infer<
  typeof updateMerchantIntegrationPayloadSchema
>;

export const deliveryPaymentSchema = z.object({
  payer: z.enum(['client', 'merchant', 'split_percent', 'split_amount']),
  // Eats returns an empty string when the payer is not split. Treat it as
  // absent so successful PATCH responses don't fail client-side validation.
  share_side: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.enum(['client', 'merchant']).optional(),
  ),
  share_percent: z.number().optional(),
  share_amount: z.number().optional(),
});
const deliveryTariffSchema = z
  .object({
    free_distance_m: z.number().optional(),
    price_per_km: z.number().optional(),
    merchant_id: z.string().optional(),
    mode: z.string().optional(),
  })
  .loose();
/** GET and PATCH responses use different envelope field names in the API. */
export const deliveryPricingSchema = z
  .object({
    delivery_pricing: deliveryTariffSchema.optional(),
    delivery_payment: deliveryPaymentSchema.optional(),
    pricing: deliveryTariffSchema.optional(),
    payment: deliveryPaymentSchema.optional(),
  })
  .loose();
export type DeliveryPricing = z.infer<typeof deliveryPricingSchema>;
/** PATCH request is flat and names the payer block `delivery_payment`. */
export const updateDeliveryPricingPayloadSchema = z.object({
  free_distance_m: z.number().optional(),
  price_per_km: z.number().optional(),
  delivery_payment: deliveryPaymentSchema.optional(),
});
export type UpdateDeliveryPricingPayload = z.infer<
  typeof updateDeliveryPricingPayloadSchema
>;

/**
 * Provider-keyed credentials body for `PUT
 * /api/v1/merchants/{merchant_id}/integrations/{provider}/credentials` — the
 * wrapper key matches the `{provider}` path param, and each provider carries
 * its own inner shape (`delever`: login pair, `iko`: api key + app id +
 * secret).
 */
export const deleverCredentialsPayloadSchema = z.object({
  client_id: z.string().min(1),
  client_secret: z.string().min(1),
});
export type DeleverCredentialsPayload = z.infer<typeof deleverCredentialsPayloadSchema>;

export const ikoCredentialsPayloadSchema = z.object({
  api_key: z.string().min(1),
  app_id: z.string().min(1),
  client_secret: z.string().min(1),
});
export type IkoCredentialsPayload = z.infer<typeof ikoCredentialsPayloadSchema>;

export const rahmatCredentialsPayloadSchema = z.object({
  client_id: z.string().min(1),
  client_secret: z.string().min(1),
});
export type RahmatCredentialsPayload = z.infer<typeof rahmatCredentialsPayloadSchema>;

export const merchantIntegrationCredentialsPayloadSchema = z.union([
  z.object({ delever: deleverCredentialsPayloadSchema }),
  z.object({ iko: ikoCredentialsPayloadSchema }),
  z.object({ rahmat: rahmatCredentialsPayloadSchema }),
]);
export type MerchantIntegrationCredentialsPayload = z.infer<
  typeof merchantIntegrationCredentialsPayloadSchema
>;

export const pharmaAuthKindSchema = z.enum([
  'AUTH_KIND_NONE',
  'AUTH_KIND_API_KEY',
  'AUTH_KIND_BASIC',
]);
export type PharmaAuthKind = z.infer<typeof pharmaAuthKindSchema>;

export const pharmaIntegrationSchema = z
  .object({
    company_id: z.string().optional(),
    has_sync: z.boolean().optional(),
    crm_type: z.string().optional(),
    endpoints: z.record(z.string(), z.string().optional()).optional(),
    // Sync intervals arrive in seconds (strings like "10800" or numbers).
    sync_intervals: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
    auth_kind: z.string().optional(),
    api_key: z.string().optional(),
    username: z.string().optional(),
    password: z.string().optional(),
    has_credentials: z.boolean().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
  })
  .loose();
export type PharmaIntegration = z.infer<typeof pharmaIntegrationSchema>;

export const updatePharmaIntegrationPayloadSchema = z.object({
  has_sync: z.boolean(),
  crm_type: z.string().min(1),
  endpoints: z.record(z.string(), z.string().optional()),
  sync_intervals: z.record(z.string(), z.string()),
  auth_kind: pharmaAuthKindSchema,
  api_key: z.string().optional(),
  username: z.string().optional(),
  password: z.string().optional(),
});
export type UpdatePharmaIntegrationPayload = z.infer<
  typeof updatePharmaIntegrationPayloadSchema
>;

/* ── Integration tokens ──────────────────────────────────────────────── */

export const integrationTokenScopeSchema = z.enum(['all', 'business', 'individual']);
export type IntegrationTokenScope = z.infer<typeof integrationTokenScopeSchema>;

export const integrationTokenStatusSchema = z.enum([
  'not_migrated',
  'issued_not_used',
  'active',
]);
export type IntegrationTokenStatus = z.infer<typeof integrationTokenStatusSchema>;

export const integrationTokenVendorSchema = z
  .object({
    vendor_id: z.number(),
    name: z.string().optional(),
    display_id: z.string().optional(),
    phone: z.string().optional(),
    is_business: z.boolean().optional(),
    has_legacy_token: z.boolean().optional(),
    migrated: z.boolean().optional(),
    status: z.string().optional(),
    issued_at: z.string().nullable().optional(),
    last_used_at: z.string().nullable().optional(),
    rotations_count: z.number().optional(),
  })
  .loose();
export type IntegrationTokenVendor = z.infer<typeof integrationTokenVendorSchema>;

export const integrationTokenSummarySchema = z
  .object({
    scope: z.string().optional(),
    vendors: z.number().optional(),
    not_migrated: z.number().optional(),
    issued_not_used: z.number().optional(),
    active: z.number().optional(),
  })
  .loose();
export type IntegrationTokenSummary = z.infer<typeof integrationTokenSummarySchema>;

export const integrationTokenListSchema = z
  .object({
    summary: integrationTokenSummarySchema.optional(),
    vendors: z
      .object({
        data: z.array(integrationTokenVendorSchema).default([]),
        count: z.number().default(0),
        page_size: z.number().optional(),
        current_page: z.number().optional(),
        last_page: z.number().optional(),
      })
      .loose(),
  })
  .loose();
export type IntegrationTokenList = z.infer<typeof integrationTokenListSchema>;

export const generateIntegrationTokenResponseSchema = z
  .object({
    display_id: z.string().optional(),
    integration_token: z.string().optional(),
    issued_at: z.string().optional(),
    name: z.string().optional(),
    phone: z.string().optional(),
    revoked_previous: z.boolean().optional(),
    token_id: z.string().optional(),
    vendor_id: z.number().optional(),
  })
  .loose();
export type GenerateIntegrationTokenResponse = z.infer<
  typeof generateIntegrationTokenResponseSchema
>;
